import React, { useState, useMemo } from 'react';

const PRODUCTS = [
  { id: 1, name: 'Wireless Noise-Cancelling Headphones', category: 'Electronics', brand: 'SoundMax', price: 8999, rating: 4.5, inStock: true, tags: ['audio','wireless','premium'] },
  { id: 2, name: 'Ergonomic Office Chair', category: 'Furniture', brand: 'ComfortPro', price: 15999, rating: 4.2, inStock: true, tags: ['office','ergonomic','sitting'] },
  { id: 3, name: 'Mechanical Keyboard RGB', category: 'Electronics', brand: 'KeyMaster', price: 5499, rating: 4.7, inStock: false, tags: ['keyboard','gaming','rgb'] },
  { id: 4, name: 'Standing Desk 140cm', category: 'Furniture', brand: 'DeskPro', price: 28999, rating: 4.3, inStock: true, tags: ['desk','standing','office'] },
  { id: 5, name: '4K Webcam Pro', category: 'Electronics', brand: 'VisionCam', price: 7499, rating: 4.1, inStock: true, tags: ['webcam','video','streaming'] },
  { id: 6, name: 'Laptop Stand Aluminium', category: 'Accessories', brand: 'ElevateDesk', price: 2999, rating: 4.6, inStock: true, tags: ['laptop','stand','aluminium'] },
  { id: 7, name: 'USB-C Hub 7-in-1', category: 'Accessories', brand: 'ConnectAll', price: 1999, rating: 4.0, inStock: false, tags: ['hub','usb','connectivity'] },
  { id: 8, name: 'Wireless Mouse Ergonomic', category: 'Electronics', brand: 'ClickEase', price: 3499, rating: 4.4, inStock: true, tags: ['mouse','wireless','ergonomic'] },
  { id: 9, name: 'Monitor Light Bar', category: 'Accessories', brand: 'LightWork', price: 2499, rating: 4.8, inStock: true, tags: ['light','monitor','eye-care'] },
  { id: 10, name: 'Foldable Bookshelf', category: 'Furniture', brand: 'ShelfMate', price: 4999, rating: 3.9, inStock: true, tags: ['shelf','books','storage'] },
  { id: 11, name: 'Noise Machine for Sleep', category: 'Wellness', brand: 'CalmNight', price: 3299, rating: 4.3, inStock: false, tags: ['sleep','noise','wellness'] },
  { id: 12, name: 'Blue Light Glasses', category: 'Wellness', brand: 'EyeShield', price: 1299, rating: 4.1, inStock: true, tags: ['glasses','blue-light','eyes'] },
];

const CATEGORIES = ['All', 'Electronics', 'Furniture', 'Accessories', 'Wellness'];

export default function SearchAndFilter() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minRating, setMinRating] = useState(0);
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid');
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);

  const results = useMemo(() => {
    let d = PRODUCTS;
    if (query.trim()) {
      const q = query.toLowerCase();
      d = d.filter(p => p.name.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q) || p.tags.some(t => t.includes(q)));
    }
    if (category !== 'All') d = d.filter(p => p.category === category);
    if (inStockOnly) d = d.filter(p => p.inStock);
    if (minPrice) d = d.filter(p => p.price >= parseFloat(minPrice));
    if (maxPrice) d = d.filter(p => p.price <= parseFloat(maxPrice));
    if (minRating > 0) d = d.filter(p => p.rating >= minRating);
    return [...d].sort((a, b) => {
      if (sortBy === 'price-asc') return a.price - b.price;
      if (sortBy === 'price-desc') return b.price - a.price;
      if (sortBy === 'rating') return b.rating - a.rating;
      return a.name.localeCompare(b.name);
    });
  }, [query, category, inStockOnly, minPrice, maxPrice, minRating, sortBy]);

  const toggleCart = (id) => setCart(c => c.includes(id) ? c.filter(x => x !== id) : [...c, id]);
  const toggleWishlist = (id) => setWishlist(w => w.includes(id) ? w.filter(x => x !== id) : [...w, id]);
  const clearFilters = () => { setQuery(''); setCategory('All'); setInStockOnly(false); setMinPrice(''); setMaxPrice(''); setMinRating(0); setSortBy('name'); };

  return (
    <div className="eval-section">
      <h2 className="eval-section-title">🔍 Product Search & Filter <span className="eval-badge-scenario">Search / Filter / Cart</span></h2>
      <p className="eval-desc">Test: live search, multi-filter combination, price range, rating filter, sort, cart/wishlist toggle, grid/list view.</p>

      <div className="sf-layout">
        <div className="sf-sidebar">
          <div className="sf-sidebar-title">Filters <button className="eval-btn eval-btn-sm" onClick={clearFilters}>Clear</button></div>
          <div className="sf-filter-group">
            <label>Category</label>
            {CATEGORIES.map(c => (
              <label key={c} className={`sf-radio ${category === c ? 'sf-radio-active' : ''}`}>
                <input type="radio" name="cat" value={c} checked={category === c} onChange={() => setCategory(c)} /> {c}
              </label>
            ))}
          </div>
          <div className="sf-filter-group">
            <label>Price Range (₹)</label>
            <div className="sf-price-row">
              <input placeholder="Min" value={minPrice} onChange={e => setMinPrice(e.target.value)} type="number" />
              <span>–</span>
              <input placeholder="Max" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} type="number" />
            </div>
          </div>
          <div className="sf-filter-group">
            <label>Min Rating: {minRating > 0 ? `${minRating}★` : 'Any'}</label>
            <input type="range" min="0" max="5" step="0.5" value={minRating} onChange={e => setMinRating(parseFloat(e.target.value))} />
          </div>
          <div className="sf-filter-group">
            <label className="sf-chk-lbl"><input type="checkbox" checked={inStockOnly} onChange={e => setInStockOnly(e.target.checked)} /> In Stock Only</label>
          </div>
        </div>

        <div className="sf-main">
          <div className="sf-toolbar">
            <input className="sf-search" placeholder="Search products, brands, tags..." value={query} onChange={e => setQuery(e.target.value)} />
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="name">Sort: Name A–Z</option>
              <option value="price-asc">Sort: Price ↑</option>
              <option value="price-desc">Sort: Price ↓</option>
              <option value="rating">Sort: Rating</option>
            </select>
            <div className="sf-view-btns">
              <button className={`eval-btn eval-btn-sm ${viewMode === 'grid' ? 'eval-btn-primary' : ''}`} onClick={() => setViewMode('grid')}>⊞</button>
              <button className={`eval-btn eval-btn-sm ${viewMode === 'list' ? 'eval-btn-primary' : ''}`} onClick={() => setViewMode('list')}>☰</button>
            </div>
            <span className="sf-count">{results.length} results</span>
            {cart.length > 0 && <span className="sf-cart-badge">🛒 {cart.length}</span>}
            {wishlist.length > 0 && <span className="sf-cart-badge" style={{background:'#fce4ec',color:'#c62828'}}>♥ {wishlist.length}</span>}
          </div>

          {results.length === 0 && <div className="sf-empty">No products match your filters.</div>}

          <div className={viewMode === 'grid' ? 'sf-grid' : 'sf-list'}>
            {results.map(p => (
              <div key={p.id} className={viewMode === 'grid' ? 'sf-card' : 'sf-list-item'}>
                <div className="sf-card-top">
                  <div className="sf-card-cat">{p.category}</div>
                  {!p.inStock && <div className="sf-out-stock">Out of Stock</div>}
                </div>
                <div className="sf-card-name">{p.name}</div>
                <div className="sf-card-brand">{p.brand}</div>
                <div className="sf-card-tags">{p.tags.map(t => <span key={t} className="sf-tag">#{t}</span>)}</div>
                <div className="sf-card-bottom">
                  <div className="sf-price">₹{p.price.toLocaleString()}</div>
                  <div className="sf-rating">{'★'.repeat(Math.round(p.rating))} <span>{p.rating}</span></div>
                  <div className="sf-actions">
                    <button className={`eval-btn eval-btn-sm ${cart.includes(p.id) ? 'eval-btn-success' : ''}`} onClick={() => toggleCart(p.id)} disabled={!p.inStock}>
                      {cart.includes(p.id) ? '✓ Added' : '🛒 Cart'}
                    </button>
                    <button className={`eval-btn eval-btn-sm ${wishlist.includes(p.id) ? 'sf-wishlist-active' : ''}`} onClick={() => toggleWishlist(p.id)}>
                      {wishlist.includes(p.id) ? '♥' : '♡'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
